import { createContext, forwardRef, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { Box, HStack, Icon, Input, Text } from "@chakra-ui/react"
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  pointerWithin,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
} from "@dnd-kit/core"
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { FaCheck, FaGripVertical, FaPlus, FaXmark } from "react-icons/fa6"
import { NativeSelectField, NativeSelectRoot } from "@hydrologiq/shared/chakra/NativeSelect"
import type { AlertConditionGroup } from "@hydrologiq/shared/components/alerts"
import { AlertSettingsCard } from "@hydrologiq/shared/components/alerts/AlertSettingsCard"
import { ALIGNED_TITLE_INPUT } from "@hydrologiq/shared/components/TitleWithChips"
import { MUTED, OriginTag, SeverityTag } from "@hydrologiq/shared/components/alerts/alertSummary"
import { OPERATORS, operatorLabel, type CustomAlertField } from "@hydrologiq/shared/components/alerts/customAlertFields"
import { ActionButton } from "@hydrologiq/shared/content/components/Button"
import type { CustomAlertDraft, CustomAlertError } from "../../hooks/useAssetAlertConfiguration"
import {
  ConditionGroup,
  LINE_HEIGHT,
  conditionLines,
  type GroupHighlight,
  type LineContainerProps,
} from "./ConditionGroup"
import {
  allConditions,
  containerOf,
  findContradictions,
  flip,
  fromConditionGroup,
  moveCondition,
  ROOT,
  toConditionGroup,
  ungroup,
  type DraftCondition,
  type DraftConditions,
} from "./conditionDraft"
import { COMPARISON_WIDTH, CONDITION_HEIGHT, FIELD_WIDTH, UnitInput } from "./UnitInput"

/** The Assets whose Readings a Custom Alert on this page can judge, with their fields. */
export interface CustomAlertFieldGroup {
  assetId: string
  /** Optgroup label in the field list */
  assetName: string
  fields: CustomAlertField[]
}

export interface CustomAlertEditorProps {
  fieldGroups: CustomAlertFieldGroup[]
  /** The alert being edited; absent for a new one */
  initial?: { name: string; conditionGroup: AlertConditionGroup }
  onSubmit: (draft: CustomAlertDraft) => Promise<CustomAlertError | null>
  onCancel: () => void
}

type DraftLine =
  | { kind: "clause"; condition: DraftCondition; number: number }
  /** An empty group's slot: drop conditions here */
  | { kind: "slot"; groupId: string }

const NAME_MAX = 80
// Two lines each: what's wrong, then what to do about it
const THIN_GROUP_MESSAGE = [
  "Each group needs at least two conditions.",
  "Add one to the group marked in red, or ungroup it.",
]
const CONTRADICTION_MESSAGE = [
  "The highlighted conditions contradict each other, so they could never all hold.",
  "Change or remove one to save.",
]
const SELECT_STYLE = { fontSize: "sm", h: CONDITION_HEIGHT, borderColor: "#D0D0D0" } as const
const ACCENT = "#39aed2"
// Hex: the theme doesn't resolve Chakra's colour tokens (red.500 rendered black)
const ERROR_RED = "#E53E3E"
const TINT = "rgba(57, 174, 210, 0.08)"

/**
 * Drop on whatever the pointer is over: a condition, a group's row, or the top-level row.
 * Nearest-centre alone let the top-level row, just under the last group, win over that
 * group's own conditions, so conditions dragged into it landed back at the top level.
 * Between rows (the gaps), fall back to the nearest.
 */
const dropUnderPointer: CollisionDetection = (args) => {
  const under = pointerWithin(args)
  return under.length > 0 ? under : closestCenter(args)
}

/** What a line needs from the row: the condition being dragged, and those that contradict. */
const EditorContext = createContext<{ activeId: string | null; contradicted: Set<string>; landedId: string | null }>({
  activeId: null,
  contradicted: new Set(),
  landedId: null,
})

// The condition that just moved glows, then fades; held still for less motion
const LANDED_GLOW = {
  "@keyframes conditionLanded": {
    "0%": { boxShadow: "0 0 0 3px rgba(57, 174, 210, 0.75)", background: "rgba(57, 174, 210, 0.18)" },
    "100%": { boxShadow: "0 0 0 3px rgba(57, 174, 210, 0)", background: "rgba(57, 174, 210, 0)" },
  },
  animation: "conditionLanded 1.3s ease-out",
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
}

// A red glow that pulses gently; held still for people who prefer less motion
const CONTRADICTION_GLOW = {
  "@keyframes contradictionGlow": {
    "0%, 100%": { boxShadow: "0 0 0 2px rgba(229, 62, 62, 0.45)" },
    "50%": { boxShadow: "0 0 0 2px rgba(229, 62, 62, 0.9), 0 0 10px rgba(229, 62, 62, 0.45)" },
  },
  animation: "contradictionGlow 1.6s ease-in-out infinite",
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
    boxShadow: "0 0 0 2px rgba(229, 62, 62, 0.8)",
  },
}

/**
 * A Custom Alert's card while it's created or edited: its name and its conditions. Conditions join with AND
 * or OR (the bracket chip flips it) and can be grouped one level deep, each group with its
 * own connective. A condition is dragged by its handle to reorder it or move it into or out
 * of a group; each group's own row adds to it or ungroups it. Checks the draft
 * before saving; server errors show on the name or under the conditions.
 */
export function CustomAlertEditor({ fieldGroups, initial, onSubmit, onCancel }: CustomAlertEditorProps) {
  const nextId = useRef(1)
  const newId = (prefix: string) => `${prefix}-${nextId.current++}`
  const blank = (): DraftCondition => ({ id: newId("condition"), field: "", operator: "gt", threshold: "" })

  const [name, setName] = useState(initial?.name ?? "")
  const [draft, setDraft] = useState<DraftConditions>(() =>
    initial ? fromConditionGroup(initial.conditionGroup, newId) : { connective: "AND", conditions: [blank()], groups: [] },
  )
  const [hovered, setHovered] = useState<GroupHighlight>(null)
  const [dragTarget, setDragTarget] = useState<number | null>(null)
  const [activeId, setActiveId] = useState<string | null>(null)
  // The group whose Ungroup is hovered, previewed in red
  const [ungroupHover, setUngroupHover] = useState<number | null>(null)
  // A condition just moved into or out of a group, marked for a moment
  const [landed, setLanded] = useState<{ conditionId: string; group: number } | null>(null)
  useEffect(() => {
    if (!landed) return
    const timer = setTimeout(() => setLanded(null), 1400)
    return () => clearTimeout(timer)
  }, [landed])
  const [nameError, setNameError] = useState<string>()
  const [conditionErrors, setConditionErrors] = useState<string[]>([])
  const [invalid, setInvalid] = useState<Set<string>>(new Set())
  const [saving, setSaving] = useState(false)

  const all = allConditions(draft)
  const contradicted = useMemo(() => findContradictions(draft), [draft])
  // A group joins two or more conditions with its AND/OR; one with fewer can't be saved
  const thinGroups = draft.groups.flatMap((g, i) => (g.conditions.length < 2 ? [i] : []))
  const groupIndex = (container: string | null) => draft.groups.findIndex((g) => g.id === container)

  const update = (id: string, patch: Partial<DraftCondition>) => {
    const apply = (list: DraftCondition[]) => list.map((c) => (c.id === id ? { ...c, ...patch } : c))
    setDraft((d) => ({ ...d, conditions: apply(d.conditions), groups: d.groups.map((g) => ({ ...g, conditions: apply(g.conditions) })) }))
  }
  const remove = (id: string) => {
    const keep = (list: DraftCondition[]) => list.filter((c) => c.id !== id)
    setDraft((d) => ({ ...d, conditions: keep(d.conditions), groups: d.groups.map((g) => ({ ...g, conditions: keep(g.conditions) })) }))
  }
  const addToGroup = (groupId: string) =>
    setDraft((d) => ({
      ...d,
      groups: d.groups.map((g) => (g.id === groupId ? { ...g, conditions: [...g.conditions, blank()] } : g)),
    }))
  // A new group starts empty, as OR: "this AND (that OR the other)" is the usual reason to group
  const addGroup = () => setDraft((d) => ({ ...d, groups: [...d.groups, { id: newId("group"), connective: "OR", conditions: [] }] }))
  const toggle = (cluster: number | null) =>
    setDraft((d) =>
      cluster === null
        ? { ...d, connective: flip(d.connective) }
        : { ...d, groups: d.groups.map((g, i) => (i === cluster ? { ...g, connective: flip(g.connective) } : g)) },
    )

  const sensors = useSensors(
    // A small threshold, so a click on the handle isn't read as a drag
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const onDragOver = ({ over }: DragOverEvent) => {
    const index = groupIndex(over ? containerOf(draft, String(over.id)) : null)
    setDragTarget(index === -1 ? null : index)
  }
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (over) {
      const from = containerOf(draft, String(active.id))
      const to = containerOf(draft, String(over.id))
      setDraft((d) => moveCondition(d, String(active.id), String(over.id)))
      // Moving between the top level and a group: show where it went
      if (from !== to) setLanded({ conditionId: String(active.id), group: groupIndex(to) })
    }
    setActiveId(null)
    setDragTarget(null)
  }

  /** Finds the draft's problems, worded for the person fixing them; true when there are none. */
  const check = () => {
    const nameOk = name.trim().length > 0 && name.trim().length <= NAME_MAX
    setNameError(nameOk ? undefined : `Give the alert a name of 1 to ${NAME_MAX} characters.`)

    const messages = new Set<string>()
    const bad = new Set<string>()
    if (all.length === 0) messages.add("Add at least one condition.")
    for (const c of all) {
      if (!c.field) {
        messages.add("Choose a field to alert on.")
        bad.add(`field-${c.id}`)
      }
      if (c.threshold.trim() === "" || !Number.isFinite(Number(c.threshold))) {
        messages.add("Enter a number for the threshold.")
        bad.add(`threshold-${c.id}`)
      }
    }
    const seen = new Set<string>()
    for (const c of all) {
      const key = `${c.field}|${c.operator}`
      if (c.field && seen.has(key)) {
        messages.add("Two conditions compare the same field the same way.")
        bad.add(`field-${c.id}`)
      }
      seen.add(key)
    }
    setConditionErrors([...messages])
    setInvalid(bad)
    return nameOk && messages.size === 0
  }

  const submit = async () => {
    if (!check()) return
    setSaving(true)
    const error = await onSubmit({ name: name.trim(), conditionGroup: toConditionGroup(draft) })
    setSaving(false)
    if (!error) return
    if (error.field === "name") setNameError(error.message)
    else setConditionErrors([error.message])
  }

  // Numbered in reading order, so "Remove condition 2" is the second one on screen
  let number = 0
  const lines = conditionLines<DraftLine>(
    draft.conditions.map((condition) => ({ kind: "clause", condition, number: ++number })),
    // Every group ends with its own row to drop conditions on or add one, always shown
    draft.groups.map((g) => [
      ...g.conditions.map((condition) => ({ kind: "clause" as const, condition, number: ++number })),
      { kind: "slot" as const, groupId: g.id },
    ]),
  )

  const lookup = (c: DraftCondition) => {
    const [assetId, metric] = c.field.split("|")
    const group = fieldGroups.find((g) => g.assetId === assetId)
    return { group, field: group?.fields.find((f) => f.metric === metric) }
  }
  const activeCondition = all.find((c) => c.id === activeId)
  /** "Genset 1 · Power above 5000 W", for the dragged copy. */
  const describe = (c: DraftCondition) => {
    const { group, field } = lookup(c)
    if (!field) return "New condition"
    const where = fieldGroups.length > 1 && group ? `${group.assetName} · ` : ""
    const unit = field.unit === "%" ? "%" : ` ${field.unit}`
    return `${where}${field.label} ${operatorLabel(c.operator)} ${c.threshold || "…"}${c.threshold ? unit : ""}`
  }

  return (
    <AlertSettingsCard
      label={initial ? `Edit ${initial.name}` : "New custom alert"}
      editing
      isEnabled={false}
      title={
        // Fills the same name slot as a saved card's, so the chips stay put when Edit opens
        <Box w="100%">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={NAME_MAX}
            aria-label="Alert name"
            // Counts towards the list's name width, so the other cards' chips stay put
            {...{ [ALIGNED_TITLE_INPUT]: "" }}
            aria-invalid={!!nameError}
            placeholder="Name this alert"
            bg="white"
            h="40px"
            fontSize="md"
            fontWeight="semibold"
            borderWidth="1.5px"
            borderColor={nameError ? ERROR_RED : "#D0D0D0"}
          />
          <ErrorText>{nameError}</ErrorText>
        </Box>
      }
      chips={
        <>
          <OriginTag custom />
          <SeverityTag severity="warning" />
        </>
      }
    >
      <Text fontSize="md" fontWeight="semibold" mb={2}>
        Conditions
      </Text>
      {/* Padded inside, and pulled back out, so a group's outline isn't clipped by the scroll */}
      <Box maxW="calc(100% + 24px)" overflowX="auto" px="12px" py="8px" mx="-12px" my="-8px">
        <DndContext
          sensors={sensors}
          collisionDetection={dropUnderPointer}
          onDragStart={({ active }) => {
            setActiveId(String(active.id))
            setHovered(null)
          }}
          onDragOver={onDragOver}
          onDragEnd={onDragEnd}
          onDragCancel={() => {
            setActiveId(null)
            setDragTarget(null)
          }}
        >
          <SortableContext items={all.map((c) => c.id)} strategy={verticalListSortingStrategy}>
            <EditorContext.Provider value={{ activeId, contradicted, landedId: landed?.conditionId ?? null }}>
              <ConditionGroup
                lines={lines}
                connective={draft.connective}
                clusterConnectives={draft.groups.map((g) => g.connective)}
                // Brackets just after the conditions, as on the locked card
                fit
                onToggle={toggle}
                highlight={dragTarget ?? hovered}
                dropTarget={activeId ? dragTarget : null}
                landed={landed && landed.group >= 0 ? landed.group : null}
                ungroupPreview={ungroupHover}
                invalidClusters={thinGroups}
                // Hover stays wired during a drag (so the layout doesn't shift) but is ignored
                onHover={(target) => {
                  if (!activeId) setHovered(target)
                }}
                LineContainer={EditorLine}
                renderLine={({ item }) =>
                  item.kind === "slot" ? (
                    <GroupRowContent
                      group={draft.groups.findIndex((g) => g.id === item.groupId) + 1}
                      onAdd={() => addToGroup(item.groupId)}
                      onUngroup={() => {
                        setDraft((d) => ungroup(d, item.groupId))
                        setUngroupHover(null)
                        setHovered(null)
                      }}
                      onUngroupHover={(on) =>
                        setUngroupHover(on ? draft.groups.findIndex((g) => g.id === item.groupId) : null)
                      }
                    />
                  ) : (
                    <>
                      <NativeSelectRoot w={FIELD_WIDTH} flexShrink={0}>
                        <NativeSelectField
                          value={item.condition.field}
                          onChange={(e) => update(item.condition.id, { field: e.target.value })}
                          aria-label={`Field for condition ${item.number}`}
                          aria-invalid={invalid.has(`field-${item.condition.id}`) || contradicted.has(item.condition.id)}
                          {...SELECT_STYLE}
                        >
                          <option value="">Choose a field</option>
                          {fieldGroups.map((g) => (
                            <optgroup key={g.assetId} label={g.assetName}>
                              {g.fields.map((f) => (
                                <option key={f.metric} value={`${g.assetId}|${f.metric}`}>
                                  {/* A closed select shows only the option, so name the Module when there are several */}
                                  {fieldGroups.length > 1 ? `${g.assetName} · ` : ""}
                                  {f.label} ({f.unit})
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </NativeSelectField>
                      </NativeSelectRoot>
                      <NativeSelectRoot w={COMPARISON_WIDTH} flexShrink={0}>
                        <NativeSelectField
                          value={item.condition.operator}
                          onChange={(e) => update(item.condition.id, { operator: e.target.value })}
                          aria-label={`Comparison for condition ${item.number}`}
                          {...SELECT_STYLE}
                        >
                          {OPERATORS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </NativeSelectField>
                      </NativeSelectRoot>
                      <UnitInput
                        value={item.condition.threshold}
                        unit={lookup(item.condition).field?.unit ?? "—"}
                        ariaLabel={`Threshold for condition ${item.number}`}
                        invalid={invalid.has(`threshold-${item.condition.id}`)}
                        onChange={(threshold) => update(item.condition.id, { threshold })}
                      />
                      <Box w="14px" flexShrink={0} display="inline-flex">
                        {all.length > 1 && (
                          <Box
                            as="button"
                            onClick={() => remove(item.condition.id)}
                            aria-label={`Remove condition ${item.number}`}
                            color={MUTED}
                            _hover={{ color: "#9B2C2C" }}
                            display="inline-flex"
                            cursor="pointer"
                          >
                            <Icon as={FaXmark} boxSize="14px" />
                          </Box>
                        )}
                      </Box>
                    </>
                  )
                }
                trailing={
                  <Box>
                    <RootAddRow
                      onAddCondition={() => setDraft((d) => ({ ...d, conditions: [...d.conditions, blank()] }))}
                      onAddGroup={addGroup}
                    />
                    {/* Shown as soon as a group is too thin, not only on save */}
                    {thinGroups.length > 0 && (
                      <Text fontSize="xs" color={ERROR_RED} mt={1} role="status">
                        {THIN_GROUP_MESSAGE.map((line) => (
                          <Box as="span" key={line} display="block">
                            {line}
                          </Box>
                        ))}
                      </Text>
                    )}
                    {/* Shown as soon as conditions clash, not only on save */}
                    {contradicted.size > 0 && (
                      <Text fontSize="xs" color={ERROR_RED} mt={1} role="status">
                        {CONTRADICTION_MESSAGE.map((line) => (
                          <Box as="span" key={line} display="block">
                            {line}
                          </Box>
                        ))}
                      </Text>
                    )}
                    {conditionErrors.map((message) => (
                      <ErrorText key={message}>{message}</ErrorText>
                    ))}
                  </Box>
                }
              />
            </EditorContext.Provider>
          </SortableContext>
          {/* A copy that follows the pointer; the original stays, dimmed, where it came from */}
          <DragOverlay>
            {activeCondition ? <DraggedCondition text={describe(activeCondition)} /> : null}
          </DragOverlay>
        </DndContext>
      </Box>
      <HStack justify="flex-end" gap={3} mt={4} pt={4} borderTop="1px solid" borderColor="#F6C48B">
        <ActionButton onClick={onCancel} context={{ label: "Cancel", description: "", variant: "secondary" }} />
        <ActionButton
          onClick={submit}
          // A contradictory alert could never fire, and a group of one or none joins nothing
          disabled={saving || contradicted.size > 0 || thinGroups.length > 0}
          context={{ label: initial ? "Save" : "Create alert", description: "", variant: "primary" }}
        >
          <Icon as={FaCheck} boxSize="12px" />
          {initial ? "Save" : "Create alert"}
        </ActionButton>
      </HStack>
    </AlertSettingsCard>
  )
}

/** A line of the editor: a draggable condition, or an empty group's drop slot. */
function EditorLine(props: LineContainerProps<DraftLine>) {
  return props.line.item.kind === "slot" ? <SlotLine {...props} /> : <ClauseLine {...props} />
}

function ClauseLine({ line, highlighted, children }: LineContainerProps<DraftLine>) {
  const item = line.item as Extract<DraftLine, { kind: "clause" }>
  const { activeId, contradicted, landedId } = useContext(EditorContext)
  const clashes = contradicted.has(item.condition.id)
  const justLanded = landedId === item.condition.id
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition } = useSortable({
    id: item.condition.id,
  })
  return (
    <Box
      ref={setNodeRef}
      h={`${LINE_HEIGHT}px`}
      display="flex"
      alignItems="center"
      gap={2}
      minW={0}
      borderRadius="5px"
      bg={clashes ? "rgba(229, 62, 62, 0.06)" : highlighted ? TINT : undefined}
      css={clashes ? CONTRADICTION_GLOW : justLanded ? LANDED_GLOW : undefined}
      data-landed={justLanded || undefined}
      opacity={activeId === item.condition.id ? 0.35 : 1}
      position="relative"
      zIndex={activeId === item.condition.id ? 2 : undefined}
      style={{
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition: [transition, "background-color 0.15s ease"].filter(Boolean).join(", "),
      }}
    >
      <Box
        as="button"
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        aria-label={`Drag condition ${item.number}`}
        color={MUTED}
        cursor={activeId ? "grabbing" : "grab"}
        display="inline-flex"
        flexShrink={0}
        px="2px"
        _hover={{ color: ACCENT }}
      >
        <Icon as={FaGripVertical} boxSize="13px" />
      </Box>
      {children}
    </Box>
  )
}

function DraggedCondition({ text }: { text: string }) {
  return (
    <Box
      h={`${LINE_HEIGHT}px`}
      display="flex"
      alignItems="center"
      gap={2}
      px={3}
      bg="white"
      borderRadius="5px"
      border="1.5px solid"
      borderColor={ACCENT}
      boxShadow="0 6px 16px rgba(0, 0, 0, 0.15)"
      cursor="grabbing"
      fontSize="sm"
      whiteSpace="nowrap"
      w="fit-content"
    >
      <Icon as={FaGripVertical} boxSize="13px" color={ACCENT} />
      {text}
    </Box>
  )
}

// The always-there rows for adding to a group, in a pale blue so they read as a place to
// build, not as a condition
const ADD_ROW_BG = "rgba(57, 174, 210, 0.06)"
const ADD_ROW_BORDER = "#9ED6E8"
const DANGER = "#d2042d"

function SlotLine({ line, highlighted, children }: LineContainerProps<DraftLine>) {
  const { setNodeRef, isOver } = useDroppable({ id: (line.item as Extract<DraftLine, { kind: "slot" }>).groupId })
  return <AddRow ref={setNodeRef} active={isOver || highlighted}>{children}</AddRow>
}

/**
 * The top level's row: add a condition or a group. Dropping a grouped condition here moves
 * it back to the top level.
 */
function RootAddRow({ onAddCondition, onAddGroup }: { onAddCondition: () => void; onAddGroup: () => void }) {
  const { setNodeRef, isOver } = useDroppable({ id: ROOT })
  return (
    <AddRow ref={setNodeRef} active={isOver}>
      <Box flex={1} display="flex" justifyContent="center">
        <LinkButton icon={FaPlus} onClick={onAddCondition}>
          Add condition
        </LinkButton>
      </Box>
      <RowDivider />
      <Box flex={1} display="flex" justifyContent="center">
        <LinkButton icon={FaPlus} onClick={onAddGroup}>
          Add group
        </LinkButton>
      </Box>
    </AddRow>
  )
}

const AddRow = forwardRef<HTMLDivElement, { active: boolean; children: ReactNode }>(function AddRow(
  { active, children },
  ref,
) {
  return (
    <Box
      ref={ref}
      // Above a group's drop-zone outline, like the conditions
      position="relative"
      h={`${LINE_HEIGHT}px`}
      display="flex"
      alignItems="center"
      justifyContent="center"
      borderRadius="5px"
      border="1.5px dashed"
      borderColor={active ? ACCENT : ADD_ROW_BORDER}
      bg={active ? TINT : ADD_ROW_BG}
      transition="all 0.15s ease"
    >
      {children}
    </Box>
  )
})

/** A group's own row: drop conditions on it, add one, or dissolve the group. */
function GroupRowContent({
  group,
  onAdd,
  onUngroup,
  onUngroupHover,
}: {
  group: number
  onAdd: () => void
  onUngroup: () => void
  onUngroupHover: (hovering: boolean) => void
}) {
  return (
    <>
      <Box flex={1} display="flex" justifyContent="center">
        <Text fontSize="sm" color={MUTED} whiteSpace="nowrap">
          Drag condition to group
        </Text>
      </Box>
      <RowDivider />
      <Box flex={1} display="flex" justifyContent="center">
        <LinkButton icon={FaPlus} label={`Add condition to group ${group}`} onClick={onAdd}>
          Add condition
        </LinkButton>
      </Box>
      <RowDivider />
      <Box flex={1} display="flex" justifyContent="center">
        <Box
          as="button"
          onClick={onUngroup}
          onMouseEnter={() => onUngroupHover(true)}
          onMouseLeave={() => onUngroupHover(false)}
          onFocus={() => onUngroupHover(true)}
          onBlur={() => onUngroupHover(false)}
          aria-label={`Ungroup group ${group}`}
          display="inline-flex"
          alignItems="center"
          gap="5px"
          fontSize="sm"
          whiteSpace="nowrap"
          color={DANGER}
          cursor="pointer"
          _hover={{ color: "#9B2C2C" }}
        >
          <UnlinkIcon />
          Ungroup
        </Box>
      </Box>
    </>
  )
}

function RowDivider() {
  return <Box alignSelf="stretch" my="8px" borderLeft="1.5px solid" borderColor={ADD_ROW_BORDER} />
}

/** A broken chain link, as the design's Ungroup button draws it. */
function UnlinkIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 17H7A5 5 0 0 1 7 7h2" />
      <path d="M15 7h2a5 5 0 0 1 0 10h-2" />
      <path d="M4 4l16 16" />
    </svg>
  )
}

function LinkButton({
  icon,
  onClick,
  label,
  children,
}: {
  icon: typeof FaPlus
  onClick: () => void
  label?: string
  children: string
}) {
  return (
    <Box
      as="button"
      onClick={onClick}
      aria-label={label}
      display="inline-flex"
      alignItems="center"
      gap="5px"
      fontSize="sm"
      whiteSpace="nowrap"
      color={ACCENT}
      cursor="pointer"
      _hover={{ color: "#0bc5ea" }}
    >
      <Icon as={icon} boxSize="11px" />
      {children}
    </Box>
  )
}

function ErrorText({ children }: { children?: string }) {
  if (!children) return null
  return (
    <Text fontSize="xs" color={ERROR_RED} mt={1}>
      {children}
    </Text>
  )
}

export default CustomAlertEditor
